/**
 * Layout shell for company users (non-superadmin) — grouped icon sidebar +
 * mobile drawer + footer identity + command palette.
 *
 * Skin: "B+" (DESIGN-SYSTEM §9). Navigation IA per IA.md §1.1: four groups
 * (Workspace / Fleet / Acquisition / Admin), Lucide icons, accent active-state
 * with a left bar, Inbox as a nav item (drawer overlay) with an unread badge,
 * and a Cmd/Ctrl+K command palette.
 *
 * The props contract (`current` / `onNavigate` / `children`) is intentionally
 * unchanged so App.tsx's hand-rolled router keeps working until the router
 * migration (a separate, later step).
 */

import { type ComponentType, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Hash,
  Inbox,
  LayoutDashboard,
  type LucideProps,
  Megaphone,
  Menu,
  MessagesSquare,
  Moon,
  Newspaper,
  RadioTower,
  Send,
  Server,
  Settings,
  Sun,
  UserCog,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import prismIcon from "@/assets/favicon.png";
import { useExitImpersonation } from "@/lib/admin";
import { useCurrentUser, useLogout } from "@/lib/auth";
import { useInboxNotifications, useUnreadCount } from "@/lib/inbox";
import { InboxDrawer } from "@/components/InboxDrawer";
import { useTheme } from "@/lib/theme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CommandPalette,
  type CommandGroup,
} from "@/components/ui/command-palette";
import { Tooltip } from "@/components/ui/tooltip";
import { useCommandPaletteShortcut } from "@/lib/use-command-palette";
import { cn } from "@/lib/cn";

type Role = "company_admin" | "company_operator" | "company_viewer";
type NavGroup = "workspace" | "fleet" | "acquisition" | "admin";

interface NavItem {
  /** Internal key — used for active-matching against the `current` prop. */
  key: string;
  href: string;
  /** i18n label key. */
  label: string;
  group: NavGroup;
  icon: ComponentType<LucideProps>;
  visibleFor: Role[];
}

// Keys / hrefs / visibleFor are preserved verbatim from the legacy nav so the
// `current` active-matching and App.tsx routing keep working. Only the
// presentation (grouping + icons + labels) changes.
const NAV: NavItem[] = [
  {
    key: "dashboard",
    href: "/",
    label: "nav.dashboard",
    group: "workspace",
    icon: LayoutDashboard,
    visibleFor: ["company_admin", "company_operator", "company_viewer"],
  },
  {
    key: "inbox",
    href: "/inbox",
    label: "nav.inbox",
    group: "workspace",
    icon: Inbox,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "accounts",
    href: "/accounts",
    label: "nav.accounts",
    group: "fleet",
    icon: Users,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "proxies",
    href: "/proxies",
    label: "nav.proxies",
    group: "fleet",
    icon: Server,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "own_chats",
    href: "/own-chats",
    label: "nav.own_chats",
    group: "fleet",
    icon: MessagesSquare,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "channels",
    href: "/channels",
    label: "nav.channels",
    group: "acquisition",
    icon: Hash,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "users",
    href: "/users",
    label: "nav.users",
    group: "acquisition",
    icon: UsersRound,
    visibleFor: ["company_admin", "company_operator", "company_viewer"],
  },
  {
    key: "inviter",
    href: "/inviter",
    label: "nav.inviter",
    group: "acquisition",
    icon: Megaphone,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "broadcasts",
    href: "/broadcasts",
    label: "nav.broadcasts",
    group: "acquisition",
    icon: RadioTower,
    visibleFor: ["company_admin", "company_operator"],
  },
  {
    key: "aggregator",
    href: "/aggregator",
    label: "nav.aggregator",
    group: "acquisition",
    icon: Newspaper,
    visibleFor: ["company_admin", "company_operator", "company_viewer"],
  },
  {
    key: "settings",
    href: "/settings",
    label: "nav.settings",
    group: "admin",
    icon: Settings,
    visibleFor: ["company_admin"],
  },
  {
    key: "team",
    href: "/team",
    label: "nav.team",
    group: "admin",
    icon: UserCog,
    visibleFor: ["company_admin"],
  },
];

const GROUP_ORDER: NavGroup[] = ["workspace", "fleet", "acquisition", "admin"];

interface Props {
  current: string;
  onNavigate: (href: string) => void;
  children: ReactNode;
}

export function CompanyShell({ current, onNavigate, children }: Props) {
  const { t, i18n } = useTranslation();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const exitImpersonation = useExitImpersonation();
  const { theme, toggle: toggleTheme } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);

  // All hooks must run unconditionally on every render — the early returns
  // below would otherwise change the hook-call order between renders, which
  // React forbids (react-hooks/rules-of-hooks).
  const unread = useUnreadCount();
  useInboxNotifications({ onClick: () => setInboxOpen(true) });
  const [paletteOpen, setPaletteOpen] = useCommandPaletteShortcut();

  const unreadTotal = unread.data?.total ?? 0;
  const dueTotal = unread.data?.follow_ups_due ?? 0;

  const effectiveRole: Role = user?.impersonating
    ? "company_admin"
    : ((user?.role ?? "company_viewer") as Role);

  const visibleNav = useMemo(
    () => NAV.filter((item) => item.visibleFor.includes(effectiveRole)),
    [effectiveRole],
  );

  // Inbox is a drawer overlay — opening it shouldn't lose the current page.
  // Other nav items navigate normally.
  const activate = (item: NavItem) => {
    if (item.key === "inbox") {
      setDrawerOpen(false);
      setInboxOpen(true);
    } else {
      setDrawerOpen(false);
      onNavigate(item.href);
    }
  };

  // Command-palette groups: Navigate (every visible item) + a couple of
  // quick actions. Built before the early returns so hook order stays stable.
  const commandGroups = useMemo<CommandGroup[]>(() => {
    const navItem = (item: NavItem) => {
      const Icon = item.icon;
      return {
        id: `nav-${item.key}`,
        label: t(item.label),
        keywords: [item.key, item.href, t(item.label)],
        icon: <Icon size={16} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => activate(item),
      };
    };

    const groups: CommandGroup[] = [
      {
        heading: t("nav.navigate"),
        items: visibleNav.map(navItem),
      },
    ];

    const quick = [] as CommandGroup["items"];
    if (visibleNav.some((i) => i.key === "accounts")) {
      quick.push({
        id: "action-add-account",
        label: t("nav.action_add_account"),
        keywords: ["accounts", "import", "add"],
        icon: <Users size={16} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => {
          setDrawerOpen(false);
          onNavigate("/accounts");
        },
      });
    }
    if (visibleNav.some((i) => i.key === "inviter")) {
      quick.push({
        id: "action-create-campaign",
        label: t("nav.action_create_campaign"),
        keywords: ["campaign", "invite", "inviter"],
        icon: <Send size={16} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => {
          setDrawerOpen(false);
          onNavigate("/inviter");
        },
      });
    }
    if (quick.length > 0) {
      groups.push({ heading: t("nav.actions"), items: quick });
    }
    return groups;
    // `activate` / `onNavigate` are stable enough for this menu; rebuild on the
    // inputs that change the rendered list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNav, t, i18n.language]);

  if (!user) return null;

  // Superadmins without an impersonation context shouldn't be in this shell —
  // App.tsx handles the routing decision; we just defend against accidents.
  if (user.is_superadmin && !user.impersonating) return null;

  const handleExitImpersonation = async () => {
    // The hook already awaits /me refetch + clears caches before resolving,
    // so by the time we navigate, App.tsx already sees the non-impersonating
    // state and routes us back to CompaniesPage.
    await exitImpersonation.mutateAsync();
    onNavigate("/");
  };

  const renderNav = () => (
    <nav className="flex-1 space-y-5 overflow-y-auto p-3">
      {GROUP_ORDER.map((group) => {
        const items = visibleNav.filter((item) => item.group === group);
        if (items.length === 0) return null;
        return (
          <div key={group} className="space-y-1">
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
              {t(`nav.group.${group}`)}
            </p>
            {items.map((item) => {
              const Icon = item.icon;
              const active = current === item.key;
              const badge =
                item.key === "inbox" && unreadTotal > 0 ? unreadTotal : null;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => activate(item)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-accent/15 font-medium text-accent"
                      : "text-fg-secondary hover:bg-bg-elevated/50 hover:text-fg-primary",
                  )}
                >
                  {/* Left accent bar for the active item. */}
                  {active && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-accent"
                    />
                  )}
                  <Icon
                    size={20}
                    strokeWidth={2}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                  <span className="flex-1 truncate text-left">
                    {t(item.label)}
                  </span>
                  {badge !== null && (
                    <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-xs font-medium leading-none text-fg-on-accent">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                  {/* Follow-ups whose day has come — in the colour of their tag. */}
                  {item.key === "inbox" && dueTotal > 0 && (
                    <Badge
                      tone="warning"
                      variant="solid"
                      title={t("inbox.counts_due", { count: dueTotal })}
                      aria-label={t("inbox.counts_due", { count: dueTotal })}
                    >
                      {dueTotal > 99 ? "99+" : dueTotal}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-bg-primary text-fg-primary">
      {/* Mobile top bar — visible only on screens <md, opens drawer */}
      <div className="flex items-center justify-between border-b border-border bg-bg-secondary px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="-ml-2 p-2 text-fg-primary"
          aria-label="Open menu"
        >
          <Menu aria-hidden="true" size={20} strokeWidth={2} />
        </button>
        {user.impersonating ? (
          <span className="truncate text-sm font-semibold">
            {user.impersonating.company_name}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <img
              src={prismIcon}
              alt=""
              aria-hidden="true"
              className="h-6 w-6 select-none rounded-md"
              draggable={false}
            />
            <span className="text-base font-semibold tracking-tight text-fg-primary">
              Prism
            </span>
          </span>
        )}
        <span className="w-8" /> {/* spacer for symmetric layout */}
      </div>

      <div className="flex flex-1">
        {/* Drawer backdrop on mobile */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-bg-primary/70 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside
          className={cn(
            "flex flex-col border-r border-border bg-bg-secondary",
            // Desktop: always visible, fixed width — and pinned to the viewport.
            // Left static, it was as tall as the page: the menu scrolled away
            // with a long table and "log out" sat at the very bottom of it.
            "md:sticky md:top-0 md:h-dvh md:w-60 md:shrink-0 md:self-start",
            // Mobile: slide-out drawer.
            "fixed inset-y-0 left-0 z-50 w-72 transform transition-transform",
            drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          )}
        >
          <div className="flex items-start justify-between gap-2 border-b border-border p-4">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <img
                src={prismIcon}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 shrink-0 select-none rounded-lg"
                draggable={false}
              />
              <span className="truncate text-lg font-semibold tracking-tight text-fg-primary">
                Prism
              </span>
            </div>
            {/* Close button — mobile drawer only. */}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="-mr-1 -mt-1 rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated/50 hover:text-fg-primary md:hidden"
              aria-label={t("inbox.close") ?? "Close"}
            >
              <X aria-hidden="true" size={18} strokeWidth={2} />
            </button>
          </div>

          {renderNav()}

          <div className="space-y-3 border-t border-border p-4">
            {user.impersonating && (
              <div className="rounded-lg border border-danger/40 bg-danger/15 px-3 py-2 text-xs text-danger">
                {t("impersonation.banner", {
                  company: user.impersonating.company_name,
                })}
              </div>
            )}

            {/* Two rows by design: who + the small toggles, then the exit
                button on its own full-width line. All three controls next to
                "Выйти из компании" never fit a 240px sidebar, and letting the
                row wrap left the red button dangling alone under the others. */}
            <div className="flex items-center gap-2">
              <p
                className="min-w-0 flex-1 truncate text-sm font-medium text-fg-secondary"
                title={user.username}
              >
                {user.username}
              </p>

              <Tooltip content={t("app.toggle_language")}>
                <button
                  type="button"
                  onClick={() =>
                    void i18n.changeLanguage(
                      i18n.language === "ru" ? "en" : "ru",
                    )
                  }
                  className="rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated/50 hover:text-fg-primary"
                  aria-label={t("app.toggle_language")}
                >
                  {i18n.language === "ru" ? "Русский" : "English"}
                </button>
              </Tooltip>

              <Tooltip content={t("app.toggle_theme")}>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="rounded-md border border-border p-1.5 text-fg-secondary hover:bg-bg-elevated/50 hover:text-fg-primary"
                  aria-label={t("app.toggle_theme")}
                >
                  {theme === "dark" ? (
                    <Sun aria-hidden="true" size={16} strokeWidth={2} />
                  ) : (
                    <Moon aria-hidden="true" size={16} strokeWidth={2} />
                  )}
                </button>
              </Tooltip>
            </div>

            {user.impersonating ? (
              <Button
                size="sm"
                variant="danger"
                className="w-full"
                onClick={handleExitImpersonation}
                disabled={exitImpersonation.isPending}
              >
                {t("impersonation.exit")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={() => logout.mutate()}
              >
                {t("dashboard.logout")}
              </Button>
            )}
          </div>
        </aside>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        groups={commandGroups}
        placeholder={t("nav.search_placeholder") ?? undefined}
        emptyText={t("nav.command_empty")}
      />

      <InboxDrawer open={inboxOpen} onClose={() => setInboxOpen(false)} />
    </div>
  );
}
